import {
  useGetEnrollmentAdminQuery,
  useGetEnrollmentQuery,
  useGetEnrollmentsAdminQuery,
  useGetEnrollmentsQuery,
  useInitiateEnrollmentAdminMutation,
  useInitiateEnrollmentMutation,
  useSyncAllEnrollmentsAdminMutation,
  useSyncAllEnrollmentsMutation,
  useSyncEnrollmentAdminMutation,
  useSyncEnrollmentMutation,
} from './api';

/**
 * Hook to get enrollments for the current provider's organization
 */
export function useEnrollments(options?: { skip?: boolean }) {
  const result = useGetEnrollmentsQuery(undefined, {
    skip: options?.skip,
  });

  return {
    enrollments: result.data?.enrollments || [],
    isLoading: result.isLoading,
    isError: result.isError,
    isFetching: result.isFetching,
    error: result.error,
    refetch: result.refetch,
  };
}

/**
 * Hook to get enrollments for an organization (admin use)
 */
export function useEnrollmentsAdmin(organizationId: string | null, options?: { skip?: boolean }) {
  const result = useGetEnrollmentsAdminQuery(
    { organizationId: organizationId! },
    {
      skip: !organizationId || options?.skip,
    },
  );

  return {
    enrollments: result.data?.enrollments || [],
    isLoading: result.isLoading,
    isError: result.isError,
    isFetching: result.isFetching,
    error: result.error,
    refetch: result.refetch,
  };
}

/**
 * Hook to get a single enrollment (provider)
 */
export function useEnrollment(enrollmentId: string | null, options?: { skip?: boolean }) {
  const result = useGetEnrollmentQuery(
    { enrollmentId: enrollmentId! },
    {
      skip: !enrollmentId || options?.skip,
    },
  );

  return {
    enrollment: result.data || null,
    isLoading: result.isLoading,
    isError: result.isError,
    isFetching: result.isFetching,
    error: result.error,
    refetch: result.refetch,
  };
}

/**
 * Hook to get a single enrollment (admin)
 */
export function useEnrollmentAdmin(
  organizationId: string | null,
  enrollmentId: string | null,
  options?: { skip?: boolean },
) {
  const result = useGetEnrollmentAdminQuery(
    {
      organizationId: organizationId!,
      enrollmentId: enrollmentId!,
    },
    {
      skip: !organizationId || !enrollmentId || options?.skip,
    },
  );

  return {
    enrollment: result.data || null,
    isLoading: result.isLoading,
    isError: result.isError,
    isFetching: result.isFetching,
    error: result.error,
    refetch: result.refetch,
  };
}


/**
 * Hook for enrollment operations (provider)
 */
export function useEnrollmentOperations() {
  const [initiate, initiateResult] = useInitiateEnrollmentMutation();
  const [sync, syncResult] = useSyncEnrollmentMutation();
  const [syncAll, syncAllResult] = useSyncAllEnrollmentsMutation();

  return {
    // Initiate enrollment
    initiate,
    initiateLoading: initiateResult.isLoading,
    initiateError: initiateResult.error,
    initiateSuccess: initiateResult.isSuccess,

    // Sync single enrollment
    sync,
    syncLoading: syncResult.isLoading,
    syncError: syncResult.error,
    syncSuccess: syncResult.isSuccess,

    // Sync all enrollments
    syncAll,
    syncAllLoading: syncAllResult.isLoading,
    syncAllError: syncAllResult.error,
    syncAllSuccess: syncAllResult.isSuccess,
  };
}

/**
 * Hook for enrollment operations (admin)
 */
export function useEnrollmentOperationsAdmin() {
  const [initiate, initiateResult] = useInitiateEnrollmentAdminMutation();
  const [sync, syncResult] = useSyncEnrollmentAdminMutation();
  const [syncAll, syncAllResult] = useSyncAllEnrollmentsAdminMutation();

  return {
    // Initiate enrollment
    initiate,
    initiateLoading: initiateResult.isLoading,
    initiateError: initiateResult.error,
    initiateSuccess: initiateResult.isSuccess,

    // Sync single enrollment
    sync,
    syncLoading: syncResult.isLoading,
    syncError: syncResult.error,
    syncSuccess: syncResult.isSuccess,

    // Sync all enrollments
    syncAll,
    syncAllLoading: syncAllResult.isLoading,
    syncAllError: syncAllResult.error,
    syncAllSuccess: syncAllResult.isSuccess,
  };
}
